import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const colorPalettes = {
  default: {
    name: 'Default',
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#ec4899',
    background: '#080b14',
    surface: '#1a1f2e',
    text: '#ffffff',
    textSecondary: '#94a3b8'
  },
  ocean: {
    name: 'Ocean',
    primary: '#0ea5e9',
    secondary: '#06b6d4',
    accent: '#14b8a6',
    background: '#0c1222',
    surface: '#1a2332',
    text: '#f0f9ff',
    textSecondary: '#7dd3fc'
  },
  sunset: {
    name: 'Sunset',
    primary: '#f97316',
    secondary: '#ef4444',
    accent: '#eab308',
    background: '#1a0a0a',
    surface: '#2d1f1f',
    text: '#fff7ed',
    textSecondary: '#fdba74'
  },
  forest: {
    name: 'Forest',
    primary: '#22c55e',
    secondary: '#16a34a',
    accent: '#84cc16',
    background: '#0a1f0a',
    surface: '#1a2f1a',
    text: '#f0fdf4',
    textSecondary: '#86efac'
  },
  midnight: {
    name: 'Midnight',
    primary: '#818cf8',
    secondary: '#6366f1',
    accent: '#a78bfa',
    background: '#0f0f1a',
    surface: '#1a1a2e',
    text: '#e2e8f0',
    textSecondary: '#94a3b8'
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [colorPalette, setColorPalette] = useState('default');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedPalette = localStorage.getItem('colorPalette') || 'default';
    setTheme(savedTheme);
    setColorPalette(savedPalette);
    document.documentElement.setAttribute('data-theme', savedTheme);
    applyColorPalette(savedPalette);
  }, []);

  const applyColorPalette = (paletteName) => {
    const palette = colorPalettes[paletteName] || colorPalettes.default;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-secondary', palette.secondary);
    root.style.setProperty('--color-accent', palette.accent);
    root.style.setProperty('--color-background', palette.background);
    root.style.setProperty('--color-surface', palette.surface);
    root.style.setProperty('--color-text', palette.text);
    root.style.setProperty('--color-text-secondary', palette.textSecondary);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const changeColorPalette = (paletteName) => {
    setColorPalette(paletteName);
    localStorage.setItem('colorPalette', paletteName);
    applyColorPalette(paletteName);
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      colorPalette, 
      changeColorPalette,
      colorPalettes 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};